import * as THREE from 'three';
import { loadCubeMap, setBackground } from 'entangled-worlds-orbiters-shared/loaders';
import { getDefaultSkybox } from 'entangled-worlds-orbiters-shared/config';
import { loadPlatformTextureCatalog } from '../api/textureCatalog.js';

/**
 * @file src/world/SceneSpace.js
 * @description The ONE owner of this voice's scene background.
 *
 * The skybox is not this repo's to load. The shared package owns the cubemap: it knows the catalog
 * the faces are named in, it knows the KTX2 path and the PNG fallback, and it knows how to take one
 * back off a scene again. So this module owns exactly one thing — WHETHER a skybox is up — and asks
 * the shared loader for everything else. A local loader here would be a second answer to a question
 * that already has one, and the local one would win at runtime, which is how it stayed on a host
 * nothing else uses and never saw a compressed texture.
 *
 * Disabling RELEASES. Six faces plus their mipmaps is the largest single block of GPU memory the
 * scene holds, and the whole point of the minimal look is that it is given back, not hidden behind
 * an opaque quad. Disabled also means the six faces are never REQUESTED in the first place when the
 * scene starts out minimal — six fewer fetches and decodes during boot, which is exactly when a
 * low-end device can least afford them.
 */

/**
 * The most recent request made of each scene.
 *
 * Faces come off the network and a toggle is a user gesture, so a scene can be asked again while its
 * own load is still in flight. A counter rather than a "does it still want one" flag, because ORDER
 * matters and not just the final answer: on → off → on leaves two loads racing, and if the older one
 * resolves last it would otherwise win. What it carries is stale — the quality that chose its
 * variant, and worse, the RENDERER its KTX2 textures were transcoded against, which in a realm is
 * replaced whenever the realm rebuilds its compositor. Textures bound to a dead context draw black.
 *
 * A load that finds its number superseded is therefore given back rather than hung on the scene.
 */
const requests = new WeakMap();

/** What the scene shows with no sky. Not `null`: the renderer clears transparent, not black. */
const BLACK = new THREE.Color(0x000000);

/**
 * Make the mesh behave the way `scene.background` did.
 *
 * A background is not an object. It is drawn from the camera's view DIRECTION, which gives it two
 * properties nothing here gets for free: it sits at infinite distance, so moving the camera cannot
 * move it, and it is not part of the scene graph, so nothing done to the scene can touch it. The
 * shared loader hands back a real mesh instead, and both properties have to be put back by hand.
 *
 * Distance first. This scene's camera never stops translating — it eases between orbit radii, and
 * focusing a moon walks it away from the origin entirely — so a box left at the origin parallaxes.
 *
 * Parentage second, and this is the one that bites hardest. The world's spin is applied as
 * `rotation.y` on the SCENE (`applyWorldAnimation` targets `scene.userData.worldRoot` and nothing
 * ever sets one), so every child of the scene turns with the world. A background never did. Writing
 * a local position into a parent that is itself turning gets both wrong at once: the sky spins with
 * the planet AND lands somewhere the camera is not.
 *
 * So the mesh's world matrix is set OUTRIGHT — camera position, no rotation, no parent — and three
 * is told to stop deriving it. `onBeforeRender` is the moment for it because `modelViewMatrix` is
 * computed from `matrixWorld` immediately after, and because the camera that matters is whichever
 * one is drawing: a realm hands each voice its own.
 *
 * @param {THREE.Mesh} mesh
 */
function pinToTheCamera(mesh) {
    // Culling is decided from `matrixWorld` BEFORE the hook below runs, so the box would be judged
    // on where it sat last frame — and judged against a matrix nothing is maintaining any more.
    mesh.frustumCulled = false;
    // Both off, or three would recompose this from `position`/`parent.matrixWorld` and hand the
    // scene's rotation straight back.
    mesh.matrixAutoUpdate = false;
    mesh.matrixWorldAutoUpdate = false;
    mesh.onBeforeRender = (renderer, scene, camera) => {
        mesh.matrixWorld.makeTranslation(camera.position.x, camera.position.y, camera.position.z);
    };
}

/**
 * Give back a cubemap that finished loading after the scene had already moved on.
 *
 * `setBackground` is the only thing that knows how to release one — mesh, materials, six textures,
 * or a CubeTexture on the PNG fallback path — and it releases what a SCENE is holding. So the orphan
 * is handed to a scene of its own and immediately taken back off it. The real scene is never
 * touched, so a load that lost the race cannot disturb the state that won.
 *
 * @param {object} resource whatever `loadCubeMap` resolved to
 */
function releaseOrphan(resource) {
    const scratch = new THREE.Scene();
    setBackground(scratch, resource);
    setBackground(scratch, null);
}

/**
 * Put the skybox up, or take it down.
 *
 * @param {THREE.Scene} scene
 * @param {{
 *   enabled: boolean,
 *   renderer?: import('three').WebGLRenderer | null,
 *   forceLow?: boolean,
 * }} options `renderer` binds the KTX2 transcoder to the context that will draw these faces — a
 *   voice in a realm shares its realm's, so it is read from the controller rather than held. Without
 *   one the shared loader falls back to PNG, which works but pays uncompressed bytes. `forceLow`
 *   asks for the small variant; leaving it undefined lets the shared loader judge the device itself.
 * @returns {Promise<void>} resolves once the skybox is up, or immediately when taking it down
 */
export async function applySpaceBackground(scene, { enabled, renderer, forceLow } = {}) {
    if (!scene) return;
    const ticket = (requests.get(scene) ?? 0) + 1;
    requests.set(scene, ticket);

    if (!enabled) {
        // Synchronous, and deliberately so: the caller gives up the renderer right after this, and
        // three.js has to free these textures through a context that is still alive.
        setBackground(scene, null);
        // Then BLACK, which `null` is not. The renderer is built with `alpha: true` and clears to a
        // transparent black, so a scene with no background clears to nothing and the page shows
        // through the canvas. The renderer does not stop immediately either — the caller sheds the
        // world first, and that is awaited — so those frames would flash whatever is behind the
        // canvas, which on a light chrome preset is white. Still one owner: the shared owner
        // releases the sky, and the state it is released INTO is named here.
        scene.background = BLACK;
        return;
    }

    // Already showing one — leave it be rather than fetching six faces to replace six identical ones.
    if (scene.__EW_SKYBOX_MESH || scene.background?.isCubeTexture) return;

    try {
        // Asked of the real catalogue, not of the shared fallback. Which sky is default is the
        // platform's to say, and answering it from a constant baked in here is the duplicate the
        // hard-coded loader was.
        const skycubeName = await getDefaultSkybox(await loadPlatformTextureCatalog());
        const resource = await loadCubeMap({ skycubeName, preferKTX2: true, forceLow, renderer });
        if (requests.get(scene) !== ticket) {
            releaseOrphan(resource);
            return;
        }
        if (resource?.mesh) pinToTheCamera(resource.mesh);
        setBackground(scene, resource);
    } catch (error) {
        // A world without its sky is still a world. The renderer clears to nothing, which is what
        // the minimal look shows anyway, so this degrades rather than taking the scene down.
        console.warn('[SceneSpace] Skybox unavailable', error);
    }
}
